/**
 * `/admin#groups` - named sets of people, who is in each, and who leads it.
 *
 * `users:read` sees every group and what it is granted; `users:write` creates,
 * renames and deletes groups and changes members and leaders. A group bound to
 * the Admin role is Root's alone to change, because joining it *is* being made
 * an administrator - the server refuses it and the database guard
 * (`admin_group_membership_guard`, migration `0020`) refuses it again.
 *
 * Leaders are members with a flag, and a group may have several. Nothing in
 * Explorer grants a permission for leading; Argus reads it (`docs/plans/argus.md`: only a
 * leader deploys a rule).
 *
 * **Assigned roles** are whole-app roles every member inherits - Energy
 * Management carrying AI Assistant User and Argus Operator is the worked example
 * in `docs/plans/access-management.md`. They are managed here, with a preview of
 * how many people a change reaches before it is saved. Object grants stay where
 * they are made - charts and dashboards from their Share dialogs, features from
 * Features - and are listed here separately, read-only.
 */
import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo, catalogAvailable, catalogAvailabilityKnown, isRootUser, myPermissions, myUserId } from '../../state/store.ts';
import { loadMe } from '../../state/me.ts';
import {
  OPEN_GROUP_EVENT,
  loadRoleCatalog,
  roleCapabilities,
  roleCatalog,
  roleLabel,
  takePendingGroup,
} from './role-catalog.ts';
import {
  createAdminGroup,
  deleteAdminGroup,
  getAdminGroup,
  listAdminGroups,
  removeGroupMember,
  searchGroupCandidates,
  setGroupMember,
  setGroupRole,
  updateAdminGroup,
} from '../../api/endpoints.ts';
import type { AdminGroup, AdminGroupDetail, AdminGroupMember } from '../../api/types.ts';
import { confirmAction } from '../../ui/confirm-dialog.ts';
import { showToast } from '../../ui/toast.ts';
import { userMessage } from '../../api/errors.ts';

const CLOSE = svg`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
</svg>`;

type Candidate = { id: string; display_name: string; email: string | null };

function when(value: string): TemplateResult {
  return html`<time datetime=${value} title=${new Date(value).toISOString()}>
    ${new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
  </time>`;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

@customElement('groups-panel')
export class GroupsPanel extends LightElement {
  private store = new StoreController(this, [myPermissions, isRootUser, catalogAvailable, catalogAvailabilityKnown, roleCatalog]);
  @state() private groups: AdminGroup[] = [];
  @state() private loading = false;
  @state() private error = '';
  @state() private search = '';
  @state() private openId: string | null = null;
  @state() private detail: AdminGroupDetail | null = null;
  @state() private pending = false;
  @state() private creating = false;
  @state() private newName = '';
  @state() private newDescription = '';
  @state() private editName = '';
  @state() private editDescription = '';
  @state() private picking = false;
  @state() private query = '';
  @state() private found: Candidate[] = [];
  private requested = false;
  private revision = 0;
  private searchTimer: number | undefined;
  private pickTimer: number | undefined;

  override willUpdate(): void {
    if (!this.requested && canDo('users:read') && catalogAvailabilityKnown.value && catalogAvailable.value) {
      this.requested = true;
      void loadRoleCatalog();
      void this.load().then(() => {
        const asked = takePendingGroup();
        if (asked) void this.openById(asked);
      });
    }
  }

  private onOpenGroup = (e: Event): void => {
    const id = (e as CustomEvent<{ id: string }>).detail?.id;
    if (id && this.requested) {
      takePendingGroup();
      void this.openById(id);
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener(OPEN_GROUP_EVENT, this.onOpenGroup);
  }

  private async openById(id: string): Promise<void> {
    const group = this.groups.find((g) => g.id === id);
    if (group && this.openId !== id) await this.open(group);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener(OPEN_GROUP_EVENT, this.onOpenGroup);
    window.clearTimeout(this.searchTimer);
    window.clearTimeout(this.pickTimer);
    this.revision++;
  }

  private get mayWrite(): boolean {
    return canDo('users:write');
  }

  /** Whether this account may change the roles of `group`; Administrator groups are Root's. */
  private mayChangeRoles(group: AdminGroup): boolean {
    return this.mayWrite && (!group.grants_admin || isRootUser.value);
  }

  /** Whether this account may change the members of `group`. */
  private mayChangeMembers(group: AdminGroup): boolean {
    return this.mayWrite && (!group.grants_admin || isRootUser.value);
  }

  private async load(): Promise<void> {
    const revision = ++this.revision;
    this.loading = true;
    this.error = '';
    try {
      const { groups } = await listAdminGroups(this.search);
      if (revision === this.revision) this.groups = groups;
    } catch (e) {
      if (revision === this.revision) this.error = userMessage(e);
    } finally {
      if (revision === this.revision) this.loading = false;
    }
  }

  private searchChanged(value: string): void {
    this.search = value;
    this.revision++;
    window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => void this.load(), 300);
  }

  /** Take a fresh detail from the server into the open row and the list line. */
  private absorb(detail: AdminGroupDetail): void {
    this.detail = detail;
    this.editName = detail.name;
    this.editDescription = detail.description ?? '';
    const { members: _m, bindings: _b, flags: _f, ...summary } = detail;
    this.groups = this.groups.some((g) => g.id === detail.id)
      ? this.groups.map((g) => (g.id === detail.id ? summary : g))
      : [...this.groups, summary].sort((a, b) => a.name.localeCompare(b.name));
  }

  private async open(group: AdminGroup): Promise<void> {
    this.picking = false;
    if (this.openId === group.id) {
      this.openId = null;
      this.detail = null;
      return;
    }
    this.openId = group.id;
    this.detail = null;
    try {
      const detail = await getAdminGroup(group.id);
      if (this.openId === group.id) this.absorb(detail);
    } catch (e) {
      showToast('error', userMessage(e));
      if (this.openId === group.id) this.openId = null;
    }
  }

  /** One write at a time, refused changes toasted, the reply taken as the truth. */
  private async run(work: () => Promise<AdminGroupDetail | void>, success?: string): Promise<boolean> {
    if (this.pending) return false;
    this.pending = true;
    try {
      const detail = await work();
      if (detail) {
        this.absorb(detail);
        // A change to a group you are in is a change to your own access.
        if (detail.members.some((m) => m.user_id === myUserId.value)) void loadMe().catch(() => {});
      }
      if (success) showToast('success', success);
      return true;
    } catch (e) {
      showToast('error', userMessage(e));
      return false;
    } finally {
      this.pending = false;
    }
  }

  private async create(): Promise<void> {
    const name = this.newName.trim();
    if (!name) return;
    let created: AdminGroupDetail | null = null;
    const ok = await this.run(async () => {
      created = await createAdminGroup(name, this.newDescription.trim() || null);
      return created;
    }, `Group ${name} created`);
    if (ok && created) {
      this.creating = false;
      this.newName = '';
      this.newDescription = '';
      this.openId = (created as AdminGroupDetail).id;
    }
  }

  private async saveSettings(group: AdminGroupDetail): Promise<void> {
    const patch: { name?: string; description?: string | null } = {};
    if (this.editName.trim() !== group.name) patch.name = this.editName.trim();
    if (this.editDescription.trim() !== (group.description ?? '')) patch.description = this.editDescription.trim() || null;
    if (!Object.keys(patch).length) return;
    await this.run(() => updateAdminGroup(group.id, patch), 'Group updated');
  }

  private async deleteGroup(group: AdminGroupDetail): Promise<void> {
    const consequences = [
      group.member_count ? plural(group.member_count, 'member') + ' will leave it' : '',
      group.roles.length ? `its roles (${group.roles.map(roleLabel).join(', ')}) will no longer reach anyone through it` : '',
      group.grant_count ? plural(group.grant_count, 'grant') + ' to it will be withdrawn' : '',
      group.flag_count ? plural(group.flag_count, 'feature rule') + ' naming it will be removed' : '',
    ].filter(Boolean);
    if (!(await confirmAction({
      title: `Delete ${group.name}?`,
      message: consequences.length
        ? `${consequences.join('; ')}. This cannot be undone from here.`
        : 'The group is empty and holds no access. This cannot be undone from here.',
      confirmLabel: 'Delete group',
      destructive: true,
    }))) return;
    const ok = await this.run(() => deleteAdminGroup(group.id), `Group ${group.name} deleted`);
    if (ok) {
      this.groups = this.groups.filter((g) => g.id !== group.id);
      this.openId = null;
      this.detail = null;
    }
  }

  private setLeader(group: AdminGroupDetail, member: AdminGroupMember, leader: boolean): void {
    void this.run(() => setGroupMember(group.id, member.user_id, leader),
      `${member.display_name} ${leader ? 'now leads' : 'no longer leads'} ${group.name}`);
  }

  private async dropMember(group: AdminGroupDetail, member: AdminGroupMember): Promise<void> {
    if (member.is_leader && !(await confirmAction({
      title: `Remove ${member.display_name}?`,
      message: `${member.display_name} leads ${group.name}. Removing them from the group ends that too.`,
      confirmLabel: 'Remove',
      destructive: true,
    }))) return;
    await this.run(() => removeGroupMember(group.id, member.user_id), `${member.display_name} removed from ${group.name}`);
  }

  private onPick(group: AdminGroupDetail, value: string): void {
    this.query = value;
    window.clearTimeout(this.pickTimer);
    if (!value.trim()) {
      this.found = [];
      return;
    }
    this.pickTimer = window.setTimeout(async () => {
      try {
        this.found = await searchGroupCandidates(group.id, value.trim());
      } catch {
        this.found = [];
      }
    }, 200);
  }

  private async addMember(group: AdminGroupDetail, person: Candidate): Promise<void> {
    this.picking = false;
    this.query = '';
    this.found = [];
    await this.run(() => setGroupMember(group.id, person.id, false), `${person.display_name} added to ${group.name}`);
  }

  private renderPicker(group: AdminGroupDetail): TemplateResult {
    return html`<div class="ff-picker">
      <input class="ff-search" type="search" placeholder="Who should join?" aria-label="Search for a person to add"
        .value=${this.query} autofocus
        @input=${(e: Event) => this.onPick(group, (e.target as HTMLInputElement).value)}
        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Escape') this.picking = false; }} />
      ${this.found.length
        ? html`<ul class="ff-results">${this.found.map((p) => html`<li>
            <button class="ff-result" @click=${() => void this.addMember(group, p)}>
              <span class="ff-result-name">${p.display_name}</span>
              <span class="ff-result-kind">${p.email ?? ''}</span>
            </button></li>`)}</ul>`
        : this.query.trim() ? html`<p class="ff-none">Nobody else matches “${this.query}”</p>` : nothing}
    </div>`;
  }

  private renderMembers(group: AdminGroupDetail): TemplateResult {
    const changeMembers = this.mayChangeMembers(group);
    const busy = this.pending;
    return html`<div class="groups-members">
      <h2>Members <span class="people-muted groups-inline">${plural(group.member_count, 'person', 'people')}${group.leaders.length ? ` · ${plural(group.leaders.length, 'leader')}` : ''}</span></h2>
      ${group.grants_admin && !isRootUser.value
        ? html`<p class="admin-note">This group grants Administrator. Only Root can change its members.</p>` : nothing}
      ${group.members.length
        ? html`<ul class="groups-member-list">${repeat(group.members, (m) => m.user_id, (m) => html`<li class="groups-member">
            <span class="groups-member-name">
              ${m.display_name}
              ${m.is_leader ? html`<span class="admin-kind groups-leader">leader</span>` : nothing}
              ${m.is_active ? nothing : html`<span class="admin-kind">inactive</span>`}
              <span class="people-muted">${m.email ?? 'No email recorded'} · added ${when(m.added_at)}${m.added_by_name ? ` by ${m.added_by_name}` : ''}</span>
            </span>
            ${this.mayWrite ? html`<label class="groups-leader-toggle" title="Leaders deploy the group's Argus rules">
              <span>Leader</span>
              <input class="admin-switch" type="checkbox" role="switch" .checked=${m.is_leader}
                aria-label=${`${m.display_name} leads ${group.name}`} ?disabled=${busy}
                @change=${(e: Event) => { const input = e.target as HTMLInputElement; const on = input.checked;
                  input.checked = m.is_leader; this.setLeader(group, m, on); }} />
            </label>` : nothing}
            ${changeMembers ? html`<button class="ff-chip-x" title="Remove ${m.display_name}" aria-label="Remove ${m.display_name}"
              ?disabled=${busy} @click=${() => void this.dropMember(group, m)}>${CLOSE}</button>` : nothing}
          </li>`)}</ul>`
        : html`<p class="admin-note">Nobody is in this group yet.</p>`}
      ${changeMembers ? html`<div class="ff-chips groups-add">
        <button class="ff-add" ?disabled=${busy} @click=${() => { this.picking = !this.picking; this.query = ''; this.found = []; }}>
          ${this.picking ? 'Cancel' : '+ add member'}
        </button>
        ${this.picking ? this.renderPicker(group) : nothing}
      </div>` : nothing}
    </div>`;
  }

  /** Confirm a role change with its reach: who gains or loses what. */
  private async toggleRole(group: AdminGroupDetail, role: string, enabled: boolean): Promise<void> {
    const name = roleLabel(role);
    const people = plural(group.members.filter((m) => m.is_active).length, 'member');
    const what = roleCapabilities(role);
    const ok = await confirmAction({
      title: `${enabled ? 'Assign' : 'Remove'} ${name} ${enabled ? 'to' : 'from'} ${group.name}?`,
      message: enabled
        ? `${people} will inherit ${name}${what.length ? `: ${what.join(', ')}` : ''}. Features still decides which features they can open.`
        : `${people} will stop inheriting ${name}. Anyone who holds it directly or through another group keeps it.`,
      confirmLabel: enabled ? `Assign ${name}` : `Remove ${name}`,
      destructive: !enabled,
    });
    if (!ok) return;
    await this.run(() => setGroupRole(group.id, role, enabled), `${name} ${enabled ? 'assigned to' : 'removed from'} ${group.name}`);
    void loadRoleCatalog(true);
  }

  private renderRoles(group: AdminGroupDetail): TemplateResult {
    const roles = (roleCatalog.value?.roles ?? []);
    const change = this.mayChangeRoles(group);
    const held = new Set(group.roles);
    const capabilities = [...new Set(group.roles.flatMap(roleCapabilities))];
    return html`<div class="groups-roles">
      <h2>Assigned roles</h2>
      <p class="admin-note">Every member inherits these, on top of their own roles. Leaving the group withdraws only what it gave.</p>
      ${roles.map((r) => html`<label class="people-toggle">
        <span>${r.display_name}${r.kind === 'admin' ? html` <span class="admin-kind is-root">root only</span>` : nothing}</span>
        <input class="admin-switch" type="checkbox" role="switch" .checked=${held.has(r.name)}
          title=${r.description ?? ''} aria-label=${`${r.display_name} for everyone in ${group.name}`}
          ?disabled=${this.pending || !change || (r.kind === 'admin' && !isRootUser.value)}
          @change=${(e: Event) => { const input = e.target as HTMLInputElement; const on = input.checked;
            input.checked = held.has(r.name); void this.toggleRole(group, r.name, on); }} />
      </label>`)}
      ${group.grants_admin && !isRootUser.value
        ? html`<p class="admin-note">This group grants Administrator. Only Root can change its roles.</p>` : nothing}
      <h2>Effective capabilities</h2>
      ${capabilities.length
        ? html`<ul class="groups-capabilities">${capabilities.map((c) => html`<li>${c}</li>`)}</ul>`
        : html`<p class="admin-note">This group gives no whole-app role.</p>`}
    </div>`;
  }

  private renderAccess(group: AdminGroupDetail): TemplateResult {
    const objects = group.bindings.filter((b) => b.scope !== 'global');
    return html`<div class="people-sources">
      <h2>Object grants</h2>
      ${objects.length
        ? html`<ul class="people-binding-list">${objects.map((b) => html`<li>
            <strong>${b.role_name}</strong> · ${b.scope === 'global' ? 'Whole app' : `${b.scope}: ${b.scope_name ?? 'Unavailable object'}`}
            <span class="people-muted">Granted ${when(b.granted_at)}${b.granted_by_name ? ` by ${b.granted_by_name}` : ''}${b.expires_at ? html` · Expires ${when(b.expires_at)}` : nothing}</span>
          </li>`)}</ul>`
        : html`<p class="admin-note">Nothing is shared with this group. Share a chart or dashboard with it from its Share dialog.</p>`}
      <h2>Features</h2>
      ${group.flags.length
        ? html`<ul class="people-binding-list">${group.flags.map((f) => html`<li>
            <strong>${f.flag_name}</strong> · ${f.allows ? 'On for this group' : 'Off for this group (exception)'}
            <span class="people-muted"><code>${f.flag_key}</code></span>
          </li>`)}</ul>`
        : html`<p class="admin-note">No feature names this group. ${canDo('flags:read') ? 'Choose “People and groups” in Features to turn one on for it.' : ''}</p>`}
    </div>`;
  }

  private renderSettings(group: AdminGroupDetail): TemplateResult {
    const busy = this.pending;
    const changed = this.editName.trim() !== group.name || this.editDescription.trim() !== (group.description ?? '');
    return html`<div class="groups-settings">
      <label>Name
        <input class="catalog-search" maxlength="80" .value=${this.editName} ?disabled=${busy}
          @input=${(e: Event) => { this.editName = (e.target as HTMLInputElement).value; }} />
      </label>
      <label>Description
        <input class="catalog-search" maxlength="500" placeholder="What this group is for" .value=${this.editDescription} ?disabled=${busy}
          @input=${(e: Event) => { this.editDescription = (e.target as HTMLInputElement).value; }}
          @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this.saveSettings(group); }} />
      </label>
      <div class="groups-settings-actions">
        <button class="catalog-btn" ?disabled=${busy || !changed || !this.editName.trim()} @click=${() => void this.saveSettings(group)}>Save</button>
        <button class="catalog-btn is-danger" ?disabled=${busy || (group.grants_admin && !isRootUser.value)}
          title=${group.grants_admin && !isRootUser.value ? 'This group grants Administrator. Only Root can delete it.' : ''}
          @click=${() => void this.deleteGroup(group)}>Delete group</button>
      </div>
    </div>`;
  }

  private renderDetail(group: AdminGroup): TemplateResult {
    const detail = this.detail?.id === group.id ? this.detail : null;
    if (!detail) return html`<p class="admin-note" role="status">Loading the group…</p>`;
    return html`<div class="people-detail">
      <div class="people-facts">
        <span>Created <strong>${when(detail.created_at)}${detail.created_by_name ? ` by ${detail.created_by_name}` : ''}</strong></span>
        <span>Updated <strong>${when(detail.updated_at)}</strong></span>
      </div>
      ${this.mayWrite ? this.renderSettings(detail) : nothing}
      <div class="people-access groups-access">
        ${this.renderMembers(detail)}
        ${this.renderRoles(detail)}
      </div>
      ${this.renderAccess(detail)}
    </div>`;
  }

  private renderCreate(): TemplateResult {
    return html`<div class="groups-new">
      <input class="catalog-search" aria-label="New group name" placeholder="Group name, e.g. Energy team" maxlength="80"
        .value=${this.newName} ?disabled=${this.pending} autofocus
        @input=${(e: Event) => { this.newName = (e.target as HTMLInputElement).value; }}
        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this.create(); if (e.key === 'Escape') this.creating = false; }} />
      <input class="catalog-search" aria-label="New group description" placeholder="Description (optional)" maxlength="500"
        .value=${this.newDescription} ?disabled=${this.pending}
        @input=${(e: Event) => { this.newDescription = (e.target as HTMLInputElement).value; }}
        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this.create(); }} />
      <button class="catalog-btn" ?disabled=${this.pending || !this.newName.trim()} @click=${() => void this.create()}>Create</button>
      <button class="catalog-btn" ?disabled=${this.pending} @click=${() => { this.creating = false; }}>Cancel</button>
    </div>`;
  }

  override render(): TemplateResult {
    if (!catalogAvailabilityKnown.value) return html`<div class="catalog-empty" role="status">Loading groups…</div>`;
    if (!catalogAvailable.value) return html`<div class="catalog-empty"><p class="catalog-empty-title">Groups need the metadata database</p><p>The database is not configured or could not be reached.</p></div>`;
    if (!canDo('users:read')) return html`<div class="catalog-empty"><p class="catalog-empty-title">You do not have access to groups</p><p>Ask Root for user administration access.</p></div>`;
    return html`<section class="admin-section people groups" aria-busy=${this.loading || this.pending}>
      <div class="activity-filters">
        <input class="catalog-search" type="search" aria-label="Search groups" placeholder="Search groups…" maxlength="100"
          .value=${this.search} @input=${(e: Event) => this.searchChanged((e.target as HTMLInputElement).value)} />
        ${this.mayWrite && !this.creating
          ? html`<button class="catalog-btn" @click=${() => { this.creating = true; }}>New group</button>` : nothing}
      </div>
      ${this.creating ? this.renderCreate() : nothing}
      <p class="admin-note people-count" role="status">${plural(this.groups.length, 'group')}${this.loading ? ' · Loading…' : ''}${this.pending ? ' · Saving…' : ''}</p>
      ${this.error
        ? html`<div class="catalog-empty" role="alert"><p class="catalog-empty-title">Groups could not be loaded</p><p>${this.error}</p></div>`
        : this.groups.length ? html`<div class="people-table-wrap"><table class="people-table">
          <thead><tr><th scope="col">Group</th><th scope="col">Members</th><th scope="col">Leaders</th><th scope="col">Access</th><th scope="col"></th></tr></thead>
          <tbody>${repeat(this.groups, (g) => g.id, (group) => html`
            <tr class=${this.openId === group.id ? 'is-open' : ''}>
              <td><button class="people-name" aria-expanded=${this.openId === group.id} aria-controls=${`group-detail-${group.id}`}
                @click=${() => void this.open(group)}>${group.name}${group.grants_admin ? html`<span class="admin-role-chip">administrator</span>` : nothing}</button>
                <span class="people-muted">${group.description ?? ''}</span></td>
              <td>${group.member_count}</td>
              <td>${group.leaders.length ? group.leaders.join(', ') : html`<span class="people-muted">None</span>`}</td>
              <td>${group.roles.length ? group.roles.map(roleLabel).join(', ') : html`<span class="people-muted">No roles</span>`}
                ${group.grant_count - group.roles.length > 0 || group.flag_count
                  ? html`<span class="people-muted">${[group.grant_count - group.roles.length > 0 ? plural(group.grant_count - group.roles.length, 'object grant') : '', group.flag_count ? plural(group.flag_count, 'feature') : ''].filter(Boolean).join(' · ')}</span>`
                  : nothing}</td>
              <td><button class="catalog-btn" aria-label=${`Details for ${group.name}`} @click=${() => void this.open(group)}>${this.openId === group.id ? 'Close' : 'Details'}</button></td>
            </tr>
            ${this.openId === group.id ? html`<tr id=${`group-detail-${group.id}`}><td colspan="5">${this.renderDetail(group)}</td></tr>` : nothing}
          `)}</tbody></table></div>`
          : html`<div class="catalog-empty"><p class="catalog-empty-title">${this.loading ? 'Loading groups…' : this.search ? 'No matching groups' : 'No groups yet'}</p>
              ${!this.loading ? html`<p>${this.search ? 'Clear the search to see every group.' : this.mayWrite ? 'Create one to share charts, dashboards and features with a team at once.' : 'Groups are created here by an administrator.'}</p>` : nothing}</div>`}
      <p class="admin-note people-help">A group's name means nothing by itself: its members get the roles assigned to it here, what is shared with it from Share dialogs, and the features that target it in Features. Leaders are members who deploy the group's Argus rules; a group may have several.</p>
    </section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'groups-panel': GroupsPanel; }
}
